import firebase from 'firebase/compat/app';
import { useEffect, useState } from 'react';
import useOfferAccess from './useOfferAccess';
import { firestore } from '../firebase';
import { Block, InitialInfo } from 'Views/Deals/contexts/document-context';

export interface Deal {
  id: string;
  createTime: Date;
  createdBy: {
    uid: string;
    displayName: string;
    email: string;
  };
  updateTime: Date;
  status: string;
  isDeleted?: boolean;
  blocks: Block[];
  initialInfo: InitialInfo;
  orgId?: string;
  invoiceContact?: string;
  invoiceEmail?: string;
  deliveryAddress?: string;
  acceptTime?: Date;
  crm?: { createdForOpportunityId?: string; draftCommandId?: string };
  pipedrive?: {
    dealId?: number | null;
    orgId?: number | null;
    dealTitle?: string | null;
    orgName?: string | null;
    syncStatus?: 'LINKED' | 'SYNCED' | 'UNLINKED' | 'ERROR';
    lastSyncError?: string | null;
    lastSyncedAt?: Date | null;
    lastAttemptAt?: Date | null;
    linkedAt?: Date | null;
    lastActivityId?: number | null;
    lastNoteId?: number | null;
  };
}

export default function useDeals({
  enabled = true,
  orgId,
}: { enabled?: boolean; orgId?: string } = {}) {
  const access = useOfferAccess(enabled);
  const [state, setState] = useState<{
    key: string;
    deals: Deal[];
    loading: boolean;
    error: Error | null;
    updatedAt: Date | null;
  }>();
  const scopeKey = JSON.stringify([enabled, access.uid, access.policy, orgId]);
  useEffect(() => {
    if (!enabled || access.loading || !access.policy) return;
    const policy = access.policy;
    let active = true;
    const sources = new Map<
      string,
      { deals: Deal[]; ready: boolean; error: Error | null; updatedAt: Date | null }
    >();
    const owners = policy.active
      ? policy.allRead
        ? ['*']
        : Array.from(new Set(policy.readOwnerIds))
      : [];
    const publish = () => {
      if (!active) return;
      const rows = Array.from(sources.values());
      const dates = rows.flatMap((row) => (row.updatedAt ? [row.updatedAt.getTime()] : []));
      setState({
        key: scopeKey,
        deals: rows
          .flatMap((row) => row.deals)
          .sort((a, b) => b.updateTime.getTime() - a.updateTime.getTime()),
        loading: rows.some((row) => !row.ready),
        error: rows.find((row) => row.error)?.error || null,
        updatedAt: dates.length ? new Date(Math.min(...dates)) : new Date(),
      });
    };
    owners.forEach((owner) =>
      sources.set(owner, { deals: [], ready: false, error: null, updatedAt: null })
    );
    publish();
    const unsubscribes = owners.map((owner) => {
      let query: firebase.firestore.Query = firestore.collection('deals');
      if (owner !== '*') query = query.where('createdBy.uid', '==', owner);
      if (orgId) query = query.where('orgId', '==', orgId);
      query = query.orderBy('updateTime', 'desc');
      return query.onSnapshot(
        (snapshot) => {
          const deals = snapshot.docs.flatMap((doc): Deal[] => {
            const data = doc.data();
            const createTime = toDateOrNull(data.createTime);
            const updateTime = toDateOrNull(data.updateTime);
            if (!createTime || !updateTime) return [];
            const pipedrive = data.pipedrive
              ? {
                  ...data.pipedrive,
                  lastSyncedAt: toDateOrNull(data.pipedrive.lastSyncedAt),
                  lastAttemptAt: toDateOrNull(data.pipedrive.lastAttemptAt),
                  linkedAt: toDateOrNull(data.pipedrive.linkedAt),
                }
              : undefined;
            return [
              {
                ...data,
                id: doc.id,
                createTime,
                updateTime,
                acceptTime: toDateOrNull(data.acceptTime) ?? undefined,
                pipedrive,
              } as Deal,
            ];
          });
          sources.set(owner, { deals, ready: true, error: null, updatedAt: new Date() });
          publish();
        },
        (error) => {
          sources.set(owner, { deals: [], ready: true, error, updatedAt: null });
          publish();
        }
      );
    });
    return () => {
      active = false;
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [enabled, scopeKey, access.loading, access.policy, orgId]);
  const current = state?.key === scopeKey && !access.loading && !access.error ? state : undefined;
  return {
    deals: current?.deals || [],
    loading:
      Boolean(enabled && access.uid) &&
      (access.loading || (!access.error && !current) || Boolean(current?.loading)),
    error: access.error || current?.error || null,
    updatedAt: current?.updatedAt || null,
  };
}

function toDateOrNull(value: any): Date | null {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return value;
  }
  if (typeof value?.toDate === 'function') {
    return value.toDate();
  }
  return null;
}
